import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import logoImg from '../assets/logo_transparent.png';

const navLinks = [
  { label: 'Home', href: '/#home' },
  { label: 'Services', href: '/#services' },
  { label: 'Products', href: '/#products' },
  { label: 'Training', href: '/#training' },
  { label: 'Projects', href: '/projects', isRoute: true },
  { label: 'About', href: '/about', isRoute: true },
  { label: 'Blog', href: '/blog', isRoute: true },
  { label: 'Contact', href: '/#contact' },
];

const careersLabel = 'Careers';

export default function Navbar({ forceOpaque = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isOpaque = forceOpaque || scrolled;

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isOpaque
          ? 'glass-card shadow-card py-3'
          : 'bg-transparent py-5'
        }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <a href="#home" className="flex items-center group">
          <div className="relative h-14 sm:h-20 w-48 sm:w-80 flex items-center">
            <img src={logoImg} alt="QuBright Logo" className="w-full h-full object-contain object-left scale-[1.35] sm:scale-[1.35] origin-left" />
          </div>
        </a>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            link.isRoute ? (
              <Link key={link.href} to={link.href} className="nav-link">
                {link.label}
              </Link>
            ) : (
              <a key={link.href} href={link.href} className="nav-link">
                {link.label}
              </a>
            )
          ))}
        </div>

        {/* Careers Pill + CTA */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/careers"
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-space font-semibold text-brand-orange border border-brand-orange/30 hover:bg-brand-orange/8 transition-all duration-200"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-brand-orange animate-pulse" />
            Careers
          </Link>
          <a href="#contact">
            <button className="btn-primary text-sm py-3 px-6">Get in Touch</button>
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 p-2 relative z-50 cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`block w-6 h-0.5 bg-brand-blue transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
          <span className={`block w-6 h-0.5 bg-brand-blue transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
          <span className={`block w-6 h-0.5 bg-brand-blue transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="md:hidden absolute top-full left-4 right-4 bg-white shadow-2xl mt-4 rounded-2xl p-6 flex flex-col gap-4 border border-slate-100 z-[999]"
          >
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="nav-link text-base font-syne font-bold text-slate-800"
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className="nav-link text-base font-syne font-bold text-slate-800"
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </a>
              )
            ))}
            <a href="#contact" onClick={() => setMenuOpen(false)} className="mt-2">
              <button className="btn-primary w-full">Get in Touch</button>
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
